/**
 * CampaignsPage
 *
 * Standalone Campaigns screen: a simple app bar titled "Campaigns" plus the
 * active-campaigns list. Mirrors Flutter's `!fromHomeScreen` branch in
 * lib/presentation/screens/campaigns/campaigns_screen.dart:48-127
 * (BaseAppBar + loading → ExpauseLoadingIndicator, empty → empty list copy,
 * else a vertical list of campaign items).
 *
 * Rendered via FlexibleHeaderList with NO flexible space (a plain title bar).
 * The started-campaign branch opens `CampaignRankingSheet` (Task 9), gated by
 * the same `startDate <= now` rule as Flutter.
 *
 * Page owns: `useBasePage`, the `screenKey` mint, and the UI invocations (the
 * "not started" info sheet, opening the ranking sheet). Hook owns the state,
 * the initial load, and the gate. See `.claude/context/presentation.md`.
 *
 * Each row renders `CampaignListItem` (Task 8); the empty state renders
 * `CampaignsEmptyState` (Task 8). The ranking button opens
 * `CampaignRankingSheet` (Task 9) once the campaign has started.
 */
import { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { Navigator } from '@/presentation/routing/Navigator';
import { FlexibleHeaderList } from '@/presentation/components/common/appBar/flexibleHeaderList/FlexibleHeaderList';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { InfoSheet } from '@/presentation/components/common/sheet/info/InfoSheet';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { CampaignEntity } from '@/domain/entities/campaign/campaignEntity';
import { useCampaigns } from './hooks/useCampaigns';
import { CampaignListItem } from './components/CampaignListItem';
import { CampaignsEmptyState } from './components/CampaignsEmptyState';
import { CampaignRankingSheet } from './components/CampaignRankingSheet';

export const CampaignsPage = () => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const location = useLocation();

  const { currentScreenKeyRef } = useKeepAlive();
  const screenKeyRef = useRef<string>(
    currentScreenKeyRef.current || generateScreenKey(location.pathname),
  );
  const screenKey = screenKeyRef.current;

  const { campaigns, isLoading, onPageInit, canOpenRanking } = useCampaigns();

  useBasePage({
    screenKey,
    onInit: onPageInit,
  });

  // Mirrors Flutter's `_onCampaignRankingButtonTap`
  // (campaigns_screen.dart:25-46): when the campaign has not started yet, show
  // the "not started" info sheet and return; otherwise open the ranking sheet.
  const handleOpenRanking = (campaign: CampaignEntity) => {
    if (!canOpenRanking(campaign)) {
      InfoSheet.show({
        title: l10n.campaigns.notStartedTitle,
        infoText: l10n.campaigns.notStartedMessage,
        screenKey,
        forceDialog: true,
        maxWidth: responsive.infoDialog.maxWidth,
      });
      return;
    }

    CampaignRankingSheet.show({
      campaign,
      screenKey,
      fixedHeight: responsive.bottomSheet.maxHeight,
    });
  };

  // Coarse lifecycle token for the page root, derived from existing state only.
  const pageStatus = isLoading
    ? 'loading'
    : campaigns.length === 0
      ? 'empty'
      : 'ready';

  // Loading gate: while loading, render a centered loading indicator instead of
  // the list, matching Flutter's full-screen loading state (campaigns_screen.dart:71-76).
  if (isLoading) {
    return (
      <div
        {...qaAttr({ id: 'campaigns', status: pageStatus })}
        style={{
          minHeight: '100vh',
          backgroundColor: theme.background,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: responsive.spacing.xl,
        }}
      >
        <ExpauseLoadingIndicator />
      </div>
    );
  }

  return (
    <FlexibleHeaderList<CampaignEntity>
      qa={{ id: 'campaigns', status: pageStatus }}
      listQa={{ id: 'campaigns-list' }}
      backgroundColor={theme.background}
      appBarOptions={{
        title: l10n.campaigns.title,
        onLeadingTap: () => Navigator.goBack(),
      }}
      items={campaigns}
      getItemKey={(campaign) => campaign.id}
      renderItem={(campaign) => (
        <CampaignListItem campaign={campaign} onOpenRanking={handleOpenRanking} />
      )}
      emptyListComponent={<CampaignsEmptyState />}
    />
  );
};
